// Server functions for the daily legal story social layer: reactions + comments.
// Reads are public; mutations require Supabase auth.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const REACTIONS = ["like", "love", "insightful", "thinking", "fire"] as const;
export type Reaction = (typeof REACTIONS)[number];

// ---------------- Reactions ----------------
export const getStorySocial = createServerFn({ method: "GET" })
  .inputValidator((input: { storyId: string }) =>
    z.object({ storyId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    // Resolve the caller (if signed in) so ownership is decided server-side and
    // raw user identifiers never leave the server.
    let viewerId: string | null = null;
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const authHeader = getRequest()?.headers?.get("authorization");
      const token = authHeader?.startsWith("Bearer ")
        ? authHeader.slice(7)
        : null;
      if (token) {
        const { data: claimsData } = await (
          supabaseAdmin as any
        ).auth.getClaims(token);
        viewerId = (claimsData?.claims?.sub as string | undefined) ?? null;
      }
    } catch {
      viewerId = null;
    }

    const [reactRes, commentRes] = await Promise.all([
      (supabaseAdmin as any)
        .from("story_reactions")
        .select("reaction,user_id")
        .eq("story_id", data.storyId),
      (supabaseAdmin as any)
        .from("story_comments")
        .select("id,user_id,author_name,body,created_at")
        .eq("story_id", data.storyId)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    const counts: Record<Reaction, number> = {
      like: 0,
      love: 0,
      insightful: 0,
      thinking: 0,
      fire: 0,
    };
    for (const r of (reactRes.data ?? []) as { reaction: Reaction }[]) {
      if (r.reaction in counts) counts[r.reaction]++;
    }
    const comments = ((commentRes.data ?? []) as Array<{
      id: string;
      user_id: string;
      author_name: string;
      body: string;
      created_at: string;
    }>).map((c) => ({
      id: c.id,
      author_name: c.author_name,
      body: c.body,
      created_at: c.created_at,
      mine: viewerId != null && c.user_id === viewerId,
    }));
    return {
      counts,
      total: (reactRes.data?.length ?? 0) as number,
      comments,
    };
  });


export const setMyReaction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { storyId: string; reaction: Reaction | null }) =>
    z
      .object({
        storyId: z.string().uuid(),
        reaction: z.enum(REACTIONS).nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.reaction === null) {
      const { error } = await (supabase as any)
        .from("story_reactions")
        .delete()
        .eq("story_id", data.storyId)
        .eq("user_id", userId);
      if (error) throw new Error(error.message);
      return { ok: true, reaction: null };
    }
    const { error } = await (supabase as any).from("story_reactions").upsert(
      {
        story_id: data.storyId,
        user_id: userId,
        reaction: data.reaction,
      },
      { onConflict: "story_id,user_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, reaction: data.reaction };
  });

export const getMyReaction = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { storyId: string }) =>
    z.object({ storyId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: row } = await (context.supabase as any)
      .from("story_reactions")
      .select("reaction")
      .eq("story_id", data.storyId)
      .eq("user_id", context.userId)
      .maybeSingle();
    return { reaction: (row?.reaction as Reaction | null) ?? null };
  });

// ---------------- Comments ----------------
export const addComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { storyId: string; body: string }) =>
    z
      .object({
        storyId: z.string().uuid(),
        body: z.string().trim().min(2).max(2000),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // pull profile name for display
    const { data: profile } = await (supabase as any)
      .from("profiles")
      .select("full_name")
      .eq("id", userId)
      .maybeSingle();
    const authorName =
      (profile?.full_name as string | undefined)?.trim() || "Member";
    const { data: row, error } = await (supabase as any)
      .from("story_comments")
      .insert({
        story_id: data.storyId,
        user_id: userId,
        author_name: authorName,
        body: data.body,
      })
      .select("id,user_id,author_name,body,created_at")
      .single();
    if (error) throw new Error(error.message);
    return { comment: row };
  });

export const deleteMyComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { commentId: string }) =>
    z.object({ commentId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any)
      .from("story_comments")
      .delete()
      .eq("id", data.commentId); // RLS allows self or admin
    if (error) throw new Error(error.message);
    return { ok: true };
  });
